import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useSendReminderForScorecardMutation } from '@/store/query/ats-applications';
import { KTag, KText } from '@/ui-library/components';
import { SanitizedText } from '@/ui/SanitizedDom';
import {
  Avatar,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  type FlexProps,
  Tooltip,
  Center,
} from '@chakra-ui/react';
import { InfoCircle } from '@kula-ai/icons/dual-tone/InfoCircle';
import { Edit05 } from '@kula-ai/icons/outline/Edit05';
import { Eye } from '@kula-ai/icons/outline/Eye';
import dayjs from 'dayjs';
import { ChevronDown } from '@kula-ai/icons/outline/ChevronDown';
import { useSelector } from 'react-redux';
import { type ScorecardType } from '@/types/interview';
import {
  getSelectionRatingStyle,
  ratingOptions,
} from '@/constants/scorecard-configs';
import { useDateFormat, useShowToast } from '@/hooks';
import { CenterSpinner, IconButton } from '@/components/ats';
import { FileAttachmentPreviewReadonly } from '@/components/formBuilder/formFields/FormEditor/FileAttachmentPreviewReadonly';
import { ScoreCardSendReminder } from '@/components/ats/collect-score-card/ScoreCardSendReminder';
import { ratingOptionsConfig } from '@/constants/scorecard-configs';

interface ScorecardHeaderProps extends Omit<FlexProps, 'children'> {
  name: string;
  avatar?: string;
  onSubmitScorecard?: () => void;
  scorecard?: ScorecardType;
  onViewScorecard?: () => void;
  skipViewScorecard?: boolean;
  skipShowingFeedback?: boolean;
  refetch: () => void;
  isAfterInterview: boolean;
}

export const ScorecardHeader = ({
  name,
  avatar,
  onSubmitScorecard = () => {},
  scorecard,
  onViewScorecard = () => {},
  skipViewScorecard = false,
  skipShowingFeedback = false,
  refetch = () => {},
  isAfterInterview = false,
  ...props
}: ScorecardHeaderProps) => {
  const {
    reviewer,
    status,
    submitted_by,
    can_edit,
    feedback = '',
    feedback_attachments = [],
    edited_at,
    edited_by,
    previous_rating,
    rating,
    reminder_count,
    last_reminder_sent_at,
  } = scorecard || {};

  const canEditScorecard = can_edit;
  const [sendReminderForScorecard, { isLoading: isScLoading }] =
    useSendReminderForScorecardMutation();
  const toast = useShowToast();
  const currUser = useSelector(selectCurrUser);
  const showSubmitScorecard =
    currUser?.id === reviewer?.id && status !== 'submitted';

  const isScorecardPending = status === 'draft';
  const isScorecardSubmitted = status === 'submitted';

  const isScorecardNotSubmittedByReviewer =
    reviewer?.id !== submitted_by?.id && status === 'submitted';

  const { dateFormat, timeFormat } = useDateFormat();
  const ratingConfig = ratingOptions?.find((item) => item?.value === rating);

  const scorecardStyles = getSelectionRatingStyle(ratingConfig);

  const handleSendReminder = async () => {
    const userScorecard = scorecard;
    const result = (await sendReminderForScorecard({
      scorecardId: userScorecard?.id,
    })) as {
      errors?: string[] | Record<string, unknown>;
      data?: unknown;
    };

    if (result?.errors?.length) {
      toast({
        title: 'Sending scorecard reminder failed',
        status: 'error',
      });
    } else {
      toast({ title: 'Scorecard reminder sent successfully' });
      refetch();
    }
  };

  const iconButtonProps = {
    iconContainerProps: {
      width: 'unset',
      height: 'unset',
      bg: 'unset',
    },
    iconProps: { width: '14px', height: '14px' },
    gap: '6px',
  };

  return (
    <Flex
      borderBottom="1px solid"
      borderColor="border-color"
      py="24px"
      mx="1rem"
      justifyContent={'space-between'}
      flexDir="column"
      {...props}
    >
      <Flex flex="1">
        <Flex gap="12px" alignItems="center" flex="1">
          <Avatar
            width="20px"
            height="20px"
            borderRadius="xs"
            src={avatar}
            name={name}
            className="avatar-sm"
          />
          <Flex flexDir="column">
            <KText variant="heading_3" isTruncated maxW="200px">
              {name}
            </KText>
            {isScorecardNotSubmittedByReviewer && (
              <KText variant="heading_4" color="secondary-600">
                Submitted by {submitted_by?.name}
              </KText>
            )}
          </Flex>
          {isScorecardPending && isAfterInterview && (
            <KTag value="Pending" color="pastel-6-dark" bg="pastel-6-light" />
          )}
          {isScorecardSubmitted && (
            <Flex
              gap="6px"
              alignItems="center"
              borderRadius="4px"
              bg={ratingConfig?.bgColor}
              p="0.125em 0.5em"
            >
              <Icon
                as={ratingConfig?.icon}
                w="24px"
                h="24px"
                color={ratingConfig?.selectedColor}
                {...scorecardStyles}
              />
              <KText size="sm-medium" color={ratingConfig?.selectedColor}>
                {ratingConfig?.label}
              </KText>
            </Flex>
          )}
          {edited_at && edited_by?.id && status === 'submitted' && (
            <KTag
              value={
                <Flex
                  as="span"
                  display="inline-flex"
                  alignItems="center"
                  gap="6px"
                >
                  <KText
                    as="span"
                    variant="paragraph"
                    fontWeight="550"
                    whiteSpace="nowrap"
                  >
                    Edited
                  </KText>
                  <Tooltip
                    label={
                      <Flex flexDir="column">
                        <KText variant="paragraph" color="base-white">
                          {dayjs(edited_at).format(
                            `${dateFormat?.label}, ${timeFormat}`
                          )}
                        </KText>
                        <KText variant="paragraph" color="base-white">
                          Edited by {edited_by?.name}
                        </KText>
                        {previous_rating && (
                          <KText variant="paragraph" color="base-white">
                            Rating: From{' '}
                            {
                              ratingOptionsConfig.find(
                                (item) => item?.value === previous_rating.from
                              )?.label
                            }{' '}
                            to{' '}
                            {
                              ratingOptionsConfig.find(
                                (item) => item?.value === previous_rating.to
                              )?.label
                            }
                          </KText>
                        )}
                      </Flex>
                    }
                    hasArrow
                    shouldWrapChildren
                  >
                    <Center as="span" display="inline-flex">
                      <Icon as={InfoCircle} />
                    </Center>
                  </Tooltip>
                </Flex>
              }
            />
          )}
        </Flex>
        {isScorecardSubmitted ? (
          <Flex gap="12px">
            {canEditScorecard && (
              <IconButton
                icon={Edit05}
                onClick={onSubmitScorecard}
                text="Edit"
                {...iconButtonProps}
              />
            )}
            {!skipViewScorecard && (
              <IconButton
                icon={Eye}
                onClick={onViewScorecard}
                text="View"
                {...iconButtonProps}
              />
            )}
          </Flex>
        ) : showSubmitScorecard ? (
          <IconButton
            icon={Edit05}
            onClick={onSubmitScorecard}
            text="Submit scorecard"
            {...iconButtonProps}
          />
        ) : !isAfterInterview ? (
          <IconButton
            icon={Edit05}
            onClick={onSubmitScorecard}
            isDisabled={!canEditScorecard}
            text="Submit scorecard yourself"
            {...iconButtonProps}
          />
        ) : (
          canEditScorecard && (
            <Flex gap="6px" alignItems="center">
              {isScLoading ? (
                <CenterSpinner h="20px" spinnerProps={{ size: 'xs' }} />
              ) : (
                <ScoreCardSendReminder
                  count={reminder_count}
                  lastReminderSentAt={last_reminder_sent_at}
                  handleSendReminder={handleSendReminder}
                />
              )}
              <Menu>
                <MenuButton>
                  <Center>
                    <Icon as={ChevronDown} color="primary-500" />
                  </Center>
                </MenuButton>
                <MenuList zIndex="5">
                  <MenuItem
                    gap="8px"
                    onClick={() => {
                      // todo handle isSomeOtherUserHasDraftScorecard
                      // if (isSomeOtherUserHasDraftScorecard) {
                      //   setCurrScorecard(cell?.row?.original);
                      //   draftScActions?.onOpen();
                      // } else {
                      //   onSubmitScorecardClick(cell?.row?.original);
                      // }
                      onSubmitScorecard();
                    }}
                    _hover={{ fontWeight: 'semibold' }}
                  >
                    <Icon as={Edit05} width="16px" height="16px" />
                    <KText
                      size="sm-medium"
                      fontWeight="inherit"
                      color={'secondary-900'}
                    >
                      Submit scorecard yourself
                    </KText>
                  </MenuItem>
                </MenuList>
              </Menu>
            </Flex>
          )
        )}
      </Flex>
      {isScorecardSubmitted && feedback && !skipShowingFeedback && (
        <Flex flexDir="column" gap="6px" mt="16px">
          <KText variant="paragraph" fontWeight="semibold">
            Feedback
          </KText>
          <SanitizedText
            html={feedback || '-'}
            variant="paragraph"
            className="editor-style"
          />
          {feedback_attachments?.length > 0 && (
            <Flex gap="12px" flexWrap="wrap" mt="8px">
              {feedback_attachments.map((file, index: number) => (
                <FileAttachmentPreviewReadonly
                  key={
                    file &&
                    typeof file === 'object' &&
                    'id' in file &&
                    file.id !== null
                      ? String(file.id)
                      : index
                  }
                  file={file}
                />
              ))}
            </Flex>
          )}
        </Flex>
      )}
    </Flex>
  );
};
